"use client";

import { ReactNode, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ZaklynNavbar } from "@/components/marketing/ZaklynNavbar";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";

interface ProtectedLayoutProps {
  children: ReactNode;
}

/**
 * ProtectedLayout:
 * - Shows global navbar
 * - Gates content behind auth (redirects to /login if not logged in)
 * - Uses robust top padding based on header height to prevent overlap
 */
export default function ProtectedLayout({ children }: ProtectedLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && !user) {
      const redirect = encodeURIComponent(location.pathname + location.search);
      navigate(`/login?redirect=${redirect}`, { replace: true });
    }
  }, [loading, user, location, navigate]);

  return (
    <>
      <ZaklynNavbar />
      <div
        className="min-h-screen"
        style={{
          // Prevent content overlapping the sticky navbar.
          // If ZaklynNavbar sets --site-header-height, we use it; otherwise fallback to 96px.
          paddingTop:
            "calc(var(--site-header-height, 96px) + env(safe-area-inset-top, 0px) + 16px)",
        }}
      >
        {loading ? (
          <div className="container mx-auto px-4 md:px-6 py-6">
            <Skeleton className="h-32 mb-4" />
            <Skeleton className="h-64" />
          </div>
        ) : user ? (
          children
        ) : (
          <div className="container mx-auto px-4 md:px-6 py-6">
            <Skeleton className="h-64" />
          </div>
        )}
      </div>
    </>
  );
}